import { CONFIG_ELEMENTS, TestAnswerFeedbackDisplayOption } from '@vedalib/editor/lib/elements'
import { rtValueToText, textToRtValue, type RichTextValue } from '@vedalib/rich-text'
import { useUpdateEffect } from 'ahooks'
import cn from 'classnames'
import lodash from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { useContext } from 'use-context-selector'
import { swapItems } from 'utils'

import { buildTestAnnouncement, useLiveFeedback } from 'components/Editor/EditorElements/courseKit/CourseKitUtils'
import Dropdown from 'components/Editor/EditorElements/courseKit/DropDownCourse'
import TestFeedback from 'components/Editor/EditorElements/courseKit/TestFeedback'
import {
  COURSE_TOOLTIP,
  COURSE_TOOLTIP_TEST_MATCHING,
} from 'components/Editor/EditorElements/courseKit/portalsCurseKit'
import { getFeedbackValue } from 'components/Editor/EditorElements/utils/getFeedbackValue'
import RichText from 'components/form/RichText/RichText'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { setQuestion } from 'services/Store/Project/actions'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getBlockState } from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'

import type { MatchingGroups } from '../../testMatchingTypes'
import TestMatchingContext from '../TestMatchingContext'
import AnswerTooltip from './AnswerTooltip'
import * as s from './MobileElement.module.scss'

const OVERLAY_STYLE = { top: 0, left: 0, right: 0, bottom: 0 }
const DELAY_ACTION = 100

const MobileElement = ({ originLeft }: { originLeft: MatchingGroups[] }) => {
  const {
    setDraggableId,
    right,
    left,
    elementId,
    block,
    validOrderMap,
    styles,
    draggableId,
    mode,
    value,
    state,
    font,
    feedback,
    isActiveElement,
    onLabelSelect,
    activeIndex,
    onChangeLabel,
    isFill,
    waiting,
  } = useContext(TestMatchingContext)
  const dispatch = useProjectDispatch()
  const [pairs, setPairs] = useState(left.reduce((acc, item) => ({ ...acc, [item.value]: -1 }), {}))
  const [isAdditionOpen, setAdditionOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const isPreview = mode.previewMode !== PreviewMode.editor
  const blockResult = useProjectContext(getBlockState, block?.uuid)?.result
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.testMatching.type,
      elementId,
    },
  )
  const feedbackValue = getFeedbackValue(value)
  const getContainer = () =>
    (isPreview
      ? document.getElementById(COURSE_TOOLTIP)
      : document.getElementById(COURSE_TOOLTIP_TEST_MATCHING)) || document.body

  const borderStyle = (direction: string) => ({
    [`border${direction}RightRadius`]: styles.pair.borderRadius,
    [`border${direction}LeftRadius`]: styles.pair.borderRadius,
    ...styles.border,
  })

  const handleLeftClick = (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
  ) => {
    setDraggableId((e.currentTarget as Element).id)
  }

  const handleRightClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const id = Number((e.currentTarget as Element).id)
      setPairs((prev) => {
        const newPrev = lodash.omit(prev, lodash.findKey(prev, (o) => o === id) || '')
        return draggableId && { ...newPrev, [draggableId]: id }
      })
      const leftId = left.findIndex((item) => item.value === draggableId)

      const newLeftItems = swapItems(left, leftId, Number((e.currentTarget as Element).id)).map(
        (item) => {
          return item.value === draggableId ? { ...item, isActive: true } : item
        },
      )
      if (isPreview) {
        setAdditionOpen(false)
      }
      const data = entityAnalyticsConfig.interacted({
        resultExtensions: {
          dragItem: draggableId,
          dropItem: right[id]?.value,
          labelDragItem: rtValueToText(left.find((item) => item.value === draggableId)?.label),
          labelDropItem: rtValueToText(right[id]?.label),
          dragIndex: leftId,
          dropIndex: id,
        },
        objectExtensions: baseObjectExtensions,
        elementId,
      })
      sendXapiData(data)
      dispatch(
        setQuestion({
          elementId,
          value: {
            value: newLeftItems.map((item) => item.value),
            shuffledItems: newLeftItems,
          },
          isReady: newLeftItems.every(({ isActive }) => isActive),
          blockId: block?.uuid || '',
        }),
      )
    },
    [
      entityAnalyticsConfig,
      block?.uuid,
      dispatch,
      draggableId,
      elementId,
      baseObjectExtensions,
      isPreview,
      left,
      right,
      sendXapiData,
    ],
  )

  const rtToText = useCallback((rt: RichTextValue) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return rt ? (rt[0] as any)?.children[0]?.text : ''
  }, [])

  const findRightLabel = useCallback(
    (value: string) => {
      const item =
        pairs[value as keyof typeof pairs] !== -1
          ? right[pairs[value as keyof typeof pairs]]
          : { label: textToRtValue('') }
      return rtToText(item?.label)
    },
    [pairs, right, rtToText],
  )

  const hideTooltip = useCallback(() => setAdditionOpen(false), [])
  const announce = useLiveFeedback('polite')

  useUpdateEffect(() => {
    if (!blockResult) {
      return
    }
    const correctPairs = left.map((leftItem) => {
      const correctIndex = validOrderMap[leftItem.value]
      const correctRightItem = correctIndex !== undefined ? right[correctIndex] : undefined
      return `${rtValueToText(leftItem.label as RichTextValue)} - ${correctRightItem ? rtValueToText(correctRightItem.label as RichTextValue) : '—'}`
    })
    const yourPairs = left.map((leftItem) => {
      const rightIndex = pairs[leftItem.value as keyof typeof pairs]
      const rightItem = rightIndex !== -1 ? right[rightIndex] : undefined
      return `${rtValueToText(leftItem.label as RichTextValue)} - ${rightItem ? rtValueToText(rightItem.label as RichTextValue) : '—'}`
    })

    announce(
      buildTestAnnouncement(
        blockResult.isValid,
        correctPairs.length ? correctPairs.join(', ') : undefined,
        yourPairs.length ? yourPairs.join(', ') : undefined,
      ),
    )
  }, [blockResult])

  useEffect(() => {
    if (!state?.value) {
      setPairs({})
    } else {
      left.forEach((item, index) => {
        if (item.isActive) {
          setPairs((prev) => ({ ...prev, [item.value]: index }))
        }
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.value])

  // const rtStyles = useElementStyle(DeviceMode.desktop, 'richText', {
  //   [DeviceMode.desktop]: { body__var: styles.text },
  // })

  return (
    <div className={s.root}>
      <Dropdown
        container={getContainer()}
        defaultPopupVisible={isAdditionOpen}
        delay={DELAY_ACTION}
        onVisibleChange={setAdditionOpen}
        overlay={
          <AnswerTooltip
            disabled={lodash.values(lodash.omit(pairs, draggableId as keyof typeof pairs))}
            font={font}
            handleRightClick={handleRightClick}
            hideTooltip={hideTooltip}
            index={currentIndex}
            selected={pairs[draggableId as keyof typeof pairs]}
            title={left.find((item) => item.value === draggableId)?.label || textToRtValue('')}
          />
        }
        overlayStyle={OVERLAY_STYLE}
        styleType='clear'
        visible={isAdditionOpen}
        flip
        shift
      >
        <div>
          {(isPreview ? originLeft : left).map((item, index) => {
            const res: { [id: string]: number } = lodash.mapValues(pairs, (val) => parseInt(val))

            return (
              <TestFeedback
                blockUuid={block?.uuid}
                feedbackValue={feedbackValue}
                font={font}
                isTrueAnswer={isPreview ? validOrderMap[item.value] === res[item.value] : true}
                key={item.value}
                label={`${rtValueToText(item.label)} - ${rtValueToText(findRightLabel(item.value))}`}
                showAnswerFb={isPreview ? !!value.answerFeedback : true}
                styles={feedback}
              >
                <div
                  aria-label={`Select ${rtValueToText(item.label) || 'item'} to match`}
                  className={s.item}
                  id={item.value}
                  key={item.value}
                  onClick={(e) => {
                    setCurrentIndex(index)
                    handleLeftClick(e)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setCurrentIndex(index)
                      handleLeftClick(e)
                    }
                  }}
                  role='button'
                  tabIndex={0}
                >
                  <div className={s.row}>
                    <div
                      className={cn(s.fact, { [s.active]: value.answerFeedback })}
                      style={{
                        ...font?.base,
                        marginBottom:
                          value.items.length - 1 !== index
                            ? styles.indents.marginBottom
                            : undefined,
                      }}
                    >
                      <div
                        className={s.topLabel}
                        onMouseDown={(e) => onLabelSelect(index.toString(), e)}
                        style={{ ...borderStyle('Top'), padding: styles.indents.padding }}
                      >
                        <RichText
                          active={isActiveElement && activeIndex === index.toString()}
                          disabled={!isFill}
                          isPreview={mode?.previewMode !== PreviewMode.editor}
                          name={`mobileMatchingLeft.${index}`}
                          onChange={(val) => onChangeLabel(val, 'leftLabel', index)}
                          styles={font}
                          value={item.label}
                          waiting={waiting}
                        />
                      </div>
                      <div
                        className={cn(s.bottomLabel, {
                          [s.empty]: lodash.isEmpty(findRightLabel(item.value)),
                        })}
                        style={{
                          ...borderStyle('Bottom'),
                          minHeight: styles.pair.borderRadius,
                          padding: styles.indents.padding,
                        }}
                      >
                        {findRightLabel(item.value)}
                      </div>
                    </div>
                    {!value.answerFeedback && (
                      <div className={s.icon}>
                        {styles.mobileIcon.backgroundImage ? (
                          <div className={s.customIcon} style={styles.mobileIcon} />
                        ) : (
                          <div className={s.arrowIcon}>
                            <Icon name='otherDown' size={KitSize.S} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </TestFeedback>
            )
          })}
        </div>
      </Dropdown>
      <TestFeedback
        blockUuid={block?.uuid}
        feedbackValue={feedbackValue}
        font={font}
        isTrueAnswer={blockResult?.isValid}
        mode={mode}
        rtProps={{
          value: value.generalFeedback,
        }}
        showFeedback={
          value.displayFeedback &&
          value.displayOption !== TestAnswerFeedbackDisplayOption.perOption &&
          !!blockResult
        }
        styles={feedback}
      />
    </div>
  )
}

export default MobileElement
